import React from "react";
import {
  Avatar,
  Box,
  Grid2,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { EGSpeedDial } from "../../../../../../MainComponents/Button/SpeedDial/SpeedDial";
import { BoldJudge, OutlineEdit, TrashCan } from "../../../../../../Icon";
import { useTranslation } from "react-i18next";
import { EGIconButton } from "../../../../../../MainComponents/Button/IconButton/IconButton";
import { useReportContext } from "../../../../../ReportContext/useReportContext";
import Comments from "../../../../Information/Comments";

export const SanctionsAndCompensations = ({
  actions,
  actionsType,
  deleteFunction,
  openModalConfirm,
  closeModalConfirm,
  hdlOpenDialog,
}) => {
  const [t] = useTranslation("report");
  const theme = useTheme();
  const { involvedType, sanctionsOptions, benefitOptions } = useReportContext();

  const actionsTaken = {
    sanctions: {
      type: "sanctions",
      color: "orange",
      colorHover: "orangeII",
      assignedActions: t("Resolution.assigned_sanctions"),
      options: sanctionsOptions,
      another: "another_sanction",
    },
    compensations: {
      type: "benefits",
      color: "vividViolet",
      colorHover: "vividVioletII",
      assignedActions: t("Resolution.benefits_granted"),
      options: benefitOptions,
      another: "another_benefit",
    },
  };

  return (
    <Box
      px={{ xs: 0.33, md: 1 }}
      maxHeight={"40vh"}
      sx={{ overflowY: "auto" }}
      className="rounded"
    >
      {actions.map((action, i) => (
        <Box
          key={action.id}
          className={`${i < actions.length - 1 ? "border-bottom" : ""}`}
          sx={{
            minHeight: "50px",
            position: "relative",
            my: "auto",
          }}
        >
          <EGSpeedDial
            sx={{
              position: "absolute",
              right: -10,
              top: 1.5,
              scale: 0.8,
              zIndex: 1,
            }}
            tooltipPlacement="top"
            actions={[
              {
                icon: <OutlineEdit color={theme.palette.blue.main} />,
                name: t("Resolution.edit"),
                direction: "top",
                onClick: () =>
                  hdlOpenDialog(actionsTaken[actionsType].type, action),
              },
              {
                icon: <TrashCan color={theme.palette.error.main} />,
                name: t("Resolution.delete"),
                direction: "top",
                onClick: () =>
                  openModalConfirm({
                    variant: "error",
                    title: t("Resolution.attention"),
                    titleColor: theme.palette.pink.main,
                    description1: t(
                      "Care.deleting_this_measure_will_permanently_erase_all_content"
                    ),
                    question: t("Care.do_you_want_continue"),
                    leftButtonText: t("Resolution.delete"),
                    leftButtonColor: "error",
                    leftButtonVariant: "outlined",
                    onClickLeft: () => deleteFunction(action),
                    rightButtonText: t("Care.cancel"),
                    rightButtonColor: "secondary",
                    onClickRight: () => closeModalConfirm(),
                    ethiquin: "megaphone",
                  }),
              },
            ]}
          />

          <ListItem dense sx={{ padding: 0, pb: 0.33 }}>
            <ListItemAvatar>
              <Avatar sx={{ backgroundColor: "transparent" }}>
                <EGIconButton
                  iconName={
                    action.confirm
                      ? "outline_profile_tick"
                      : "outline_profile_remove"
                  }
                  color={
                    action.confirm
                      ? actionsTaken[actionsType].color
                      : "blueGray500"
                  }
                  title={
                    action.confirm
                      ? t("Resolution.confirmed")
                      : t("Resolution.unconfirmed")
                  }
                  colorHover={
                    action.confirm
                      ? actionsTaken[actionsType].colorHover
                      : "blueGray700"
                  }
                />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={
                <Box
                  display="flex"
                  alignItems="center"
                  flexWrap="wrap"
                  maxWidth={"90%"}
                >
                  <Typography
                    mr={0.5}
                    color="secondary"
                    fontWeight={600}
                    variant="body1"
                    component="span"
                  >
                    {`${involvedType[action.kind]}: `}
                  </Typography>
                  <Typography
                    color={actionsTaken[actionsType].color}
                    variant="body1"
                    component="span"
                    fontWeight={700}
                    maxWidth={"90%"}
                    textOverflow={""}
                  >
                    {action.name}
                  </Typography>
                </Box>
              }
              secondary={
                <Stack
                  spacing={2.5}
                  display="flex"
                  alignItems="center"
                  justifyContent="normal"
                  flexDirection="row"
                  flexWrap="wrap"
                  py={0.5}
                  maxWidth={"90%"}
                  component={"span"}
                >
                  <Typography variant="body2" component="span">
                    {involvedType[action.kind || action.rol]}
                  </Typography>
                  {action.additional && (
                    <Typography variant="body2" component="span">{`${t(
                      "Resolution.employee_number"
                    )}: ${action.additional}`}</Typography>
                  )}
                  {action.relationship && (
                    <Typography variant="body2" component="span">{`${t(
                      "Resolution.job_position"
                    )}: ${action.relationship}`}</Typography>
                  )}
                  {action.details && (
                    <Typography variant="body2" component="span">{`${t(
                      "Resolution.area"
                    )}: ${action.details}`}</Typography>
                  )}
                </Stack>
              }
            />
          </ListItem>

          <Grid2 container mb={0.5}>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <List
                subheader={<li />}
                dense
                sx={{
                  px: 0.5,
                  maxHeight: "25vh",
                  overflow: "auto",
                  "& ul": { padding: 0 },
                }}
              >
                <li>
                  <ul>
                    <ListSubheader
                      component={"h6"}
                      sx={{
                        px: 0.5,
                        py: 0,
                        lineHeight: "15px",
                        width: "100%",
                      }}
                    >
                      {`${actionsTaken[actionsType].assignedActions}:`}
                    </ListSubheader>
                    {action[actionsTaken[actionsType].type] &&
                      action[actionsTaken[actionsType].type].map(
                        (action, i) => (
                          <ListItem sx={{ px: 1, py: 0 }} key={`action-${i}`}>
                            <ListItemIcon sx={{ minWidth: "25px" }}>
                              <BoldJudge
                                color={
                                  theme.palette[
                                    actionsTaken[actionsType]?.color
                                  ].main
                                }
                                size={20}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                typeof action === "object" && action?.label
                                  ? action.label
                                  : actionsTaken[actionsType]?.options.find(
                                      (option) => option.value === action
                                    )?.label || action
                              }
                              primaryTypographyProps={{ variant: "body1" }}
                            />
                          </ListItem>
                        )
                      )}
                    {action.another &&
                      action.another.map((another) => (
                        <ListItem
                          sx={{ px: 1, py: 0 }}
                          key={`another-${another.id}`}
                        >
                          <ListItemIcon sx={{ minWidth: "25px" }}>
                            <BoldJudge
                              color={
                                theme.palette[actionsTaken[actionsType]?.color]
                                  .main
                              }
                              size={20}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={another[actionsTaken[actionsType].another]}
                            primaryTypographyProps={{ variant: "body1" }}
                          />
                        </ListItem>
                      ))}
                  </ul>
                </li>
              </List>
            </Grid2>
            {action.comment && (
              <Grid2 size={{ xs: 12, md: 6 }} px={1}>
                <Typography variant="h6" color="secondary">
                  {`${t("Resolution.details")}:`}
                </Typography>
                <Comments
                  text={action.comment}
                  background="white"
                  padding={1}
                  maxLength={250}
                  border="blueGray50"
                  rounded
                />
              </Grid2>
            )}
          </Grid2>
          <Stack direction="column" sx={{ px: 1, py: 0.5 }}>
            {action.determination_date && (
              <Typography variant="body1" component="span">
                {`${t("Resolution.determination_date")}: ${
                  action.determination_date
                }`}
              </Typography>
            )}
          </Stack>
          <Stack direction="column" sx={{ px: 1, py: 0.5 }}>
            {action.execution_date && (
              <Typography variant="body1" component="span">
                {`${t("Resolution.execution_date")}: ${action.execution_date}`}
              </Typography>
            )}
          </Stack>
        </Box>
      ))}
    </Box>
  );
};
